import { useCallback, useEffect, useRef, useState } from 'react'
import Markdown from 'react-markdown'
import { theme } from '../theme'
import { Ic } from '../icons'
import { api } from '../api'
import { navigate as routeTo } from '../router'
import { validateNavigatePath } from '../routes'
import type { ChatConversation, ChatMessage, ChatPart } from '../types'
import { AlertDialog, ConfirmDialog } from './Dialog'

// ============================================================================
// SSE consumer
// ============================================================================

type SSEEvent = { seq: number; type: string; data: any }

// consumeSSE reads the message stream, surviving mid-stream connection drops.
// The backend keeps each message resumable for ~60s after the driver finishes
// and honors Last-Event-ID, so on a non-aborted drop we reconnect with the
// highest seq seen and the server replays only what we missed — turning a
// transient network blip into an invisible reconnect rather than a dead stream.
async function consumeSSE(
  url: string,
  onEvent: (e: SSEEvent) => void,
  signal: AbortSignal,
): Promise<void> {
  let lastId = -1
  let terminal = false
  const maxAttempts = 8
  for (let attempt = 0; !terminal && !signal.aborted; attempt++) {
    try {
      const headers: Record<string, string> = {}
      if (lastId >= 0) headers['Last-Event-ID'] = String(lastId)
      const res = await fetch(url, { credentials: 'same-origin', signal, headers })
      if (!res.ok || !res.body) throw new Error(`stream ${res.status}`)
      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buf = ''
      let curId = -1
      let curEvent = 'message'
      let curData = ''
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read()
        if (done) break
        buf += decoder.decode(value, { stream: true })
        let idx
        while ((idx = buf.indexOf('\n')) >= 0) {
          const line = buf.slice(0, idx).replace(/\r$/, '')
          buf = buf.slice(idx + 1)
          if (line === '') {
            if (curData) {
              let parsed: any = curData
              try { parsed = JSON.parse(curData) } catch { /* keep raw */ }
              if (curId >= 0) lastId = curId
              if (curEvent === 'message_complete' || curEvent === 'error') terminal = true
              onEvent({ seq: curId, type: curEvent, data: parsed })
            }
            curId = -1; curEvent = 'message'; curData = ''
            continue
          }
          const colon = line.indexOf(':')
          if (colon < 0) continue
          const field = line.slice(0, colon)
          const value = line.slice(colon + 1).replace(/^ /, '')
          if (field === 'id') curId = parseInt(value, 10)
          else if (field === 'event') curEvent = value
          else if (field === 'data') curData += (curData ? '\n' : '') + value
        }
      }
    } catch {
      if (signal.aborted) return
      // Transport error mid-stream — fall through to reconnect/backoff.
    }
    if (terminal || signal.aborted) return
    if (attempt >= maxAttempts - 1) return // grace window likely gone; give up quietly
    const delay = Math.min(1000 * 2 ** Math.floor(attempt / 2), 5000)
    await new Promise<void>((resolve) => {
      const t = setTimeout(resolve, delay)
      signal.addEventListener('abort', () => { clearTimeout(t); resolve() }, { once: true })
    })
  }
}

// ============================================================================
// Client-side tool dispatch
//   The backend parks tool execution for tools flagged `clientSide: true` and
//   emits a `client_tool_call` SSE event. We resolve it here against the
//   live SPA state (router catalog etc.) and POST the result back so the
//   backend can feed it to the model as the tool's actual result.
// ============================================================================

// Agent-driven navigation is a tap-to-go hand-off. Rather than changing the
// route on a timer, we surface the navigate card and let it pulse for attention
// *indefinitely* until the user taps it — only then does the chat collapse and
// the route change. This puts the user in control of when (and whether) the
// page moves under them. Tunable here.
const NAV_PULSE_DELAY = 250        // beat before the card starts pulsing
const NAV_PULSE_DURATION = 700     // one pulse, ms (drives the keyframe below)
const NAV_ROUTE_DELAY = 500        // after the tap-driven collapse, before the route changes

function resolveClientTool(
  messageID: string,
  data: { id: string; name: string; args: unknown },
  setPendingNav: (nav: { callId: string; path: string } | null) => void,
) {
  const post = (body: { result?: unknown; error?: string }) => {
    api.chatToolResult(messageID, { tool_call_id: data.id, ...body })
      .catch(e => console.error('tool-result POST', e))
  }
  switch (data.name) {
    case 'navigate': {
      const args = (data.args ?? {}) as { path?: unknown }
      const v = validateNavigatePath(args.path)
      if (!v.ok) { post({ error: v.error }); return }
      // Report success to the agent immediately — the navigation is queued and
      // will happen once the user taps the card; the visual hand-off shouldn't
      // block the turn from completing.
      post({ result: { ok: true, path: v.path } })
      // Arm the tap-to-go card. It pulses until the user taps it (see the
      // navigate handler in ChatPanel), which is what actually routes.
      setTimeout(() => setPendingNav({ callId: data.id, path: v.path }), NAV_PULSE_DELAY)
      return
    }
    default:
      post({ error: `unknown client tool ${JSON.stringify(data.name)}` })
  }
}

// ============================================================================
// Stream → parts reducer
//   Applies an SSE event to an assistant message's parts array.
// ============================================================================

function applyEvent(parts: ChatPart[], e: SSEEvent): ChatPart[] {
  switch (e.type) {
    case 'text_delta': {
      const last = parts[parts.length - 1]
      if (last && last.type === 'text') {
        const next = [...parts]
        next[next.length - 1] = { type: 'text', text: last.text + e.data.text }
        return next
      }
      return [...parts, { type: 'text', text: e.data.text }]
    }
    case 'tool_call_start':
      return [...parts, {
        type: 'tool_call', id: e.data.id, name: e.data.name,
        args: undefined, status: 'running',
      }]
    case 'tool_call_args_delta': {
      // Args are streamed as JSON text; we present them after tool_call_complete.
      // Stash incremental string on a sidecar field via _argsRaw.
      const i = parts.findIndex(p => p.type === 'tool_call' && p.id === e.data.id)
      if (i < 0) return parts
      const next = [...parts]
      const p = next[i] as any
      next[i] = { ...p, _argsRaw: (p._argsRaw ?? '') + (e.data.delta ?? '') }
      return next
    }
    case 'tool_call_complete': {
      const i = parts.findIndex(p => p.type === 'tool_call' && p.id === e.data.id)
      if (i < 0) return parts
      const next = [...parts]
      next[i] = { ...(next[i] as any), args: e.data.args }
      return next
    }
    case 'tool_result': {
      const i = parts.findIndex(p => p.type === 'tool_call' && p.id === e.data.tool_call_id)
      const next = [...parts]
      if (i >= 0) {
        const p = next[i] as Extract<ChatPart, { type: 'tool_call' }>
        next[i] = { ...p, status: e.data.error ? 'failed' : 'succeeded' }
      }
      next.push({
        type: 'tool_result',
        tool_call_id: e.data.tool_call_id,
        result: e.data.result,
        error: e.data.error,
      })
      return next
    }
    default:
      return parts
  }
}

// ============================================================================
// Chat bubble entry + panel
// ============================================================================

type Layout = 'mobile' | 'docked' | 'maximized'

export function ChatBubble({ mobile }: { mobile: boolean }) {
  const [open, setOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [visible, setVisible] = useState(false)
  const [maximized, setMaximized] = useState(false)
  const [hasUnread, setHasUnread] = useState(false)
  const [pending, setPending] = useState(false)
  // Id of an unread reply in a real conversation (no greeting to generate, so
  // the panel re-opens it directly). Null when the nudge is a pending tour.
  const [unreadConvId, setUnreadConvId] = useState<string | null>(null)
  const layout: Layout = mobile ? 'mobile' : maximized ? 'maximized' : 'docked'

  // Two-phase mount: mount on first open, then flip visible=true next frame so
  // CSS transitions run. On close, flip visible=false but keep the panel
  // mounted so its conversation/draft state survives a collapse.
  useEffect(() => {
    if (open) {
      setMounted(true)
      setHasUnread(false)
      // Double rAF: first frame commits the mounted DOM with visible=false so
      // the browser paints the starting styles; second frame flips visible=true
      // so the CSS transition actually runs. A single rAF can batch into the
      // initial paint and the browser skips the transition.
      let id2 = 0
      const id1 = requestAnimationFrame(() => {
        id2 = requestAnimationFrame(() => setVisible(true))
      })
      return () => { cancelAnimationFrame(id1); cancelAnimationFrame(id2) }
    }
    setVisible(false)
  }, [open])

  // The callbacks below are passed to ChatPanel, where they end up in the
  // SSE-stream useEffect's dependency list. If their identity changes mid-
  // stream (e.g. `setOpen(false)` flipping `open` while a turn is in flight)
  // the effect would tear down the SSE connection and reconnect, which makes
  // the backend replay tool_call_start events and leaves ghost "running"
  // cards in the UI. Read the latest props via refs so the callbacks stay
  // referentially stable for the lifetime of the panel.
  const stateRef = useRef({ mobile, maximized, open })
  stateRef.current = { mobile, maximized, open }

  // Once per load, ask the server whether there's anything worth nudging about
  // — a pending guided tour, or an unread reply in a real conversation. This is
  // side-effect-free: no greeting is generated and nothing is persisted until
  // the user actually opens the chat (see ChatPanel's greeting trigger). The ref
  // guard keeps React 18 StrictMode's double-invoked effect from firing twice.
  const sessionStartedRef = useRef(false)
  useEffect(() => {
    if (sessionStartedRef.current) return
    sessionStartedRef.current = true
    api.chatSessionStart()
      .then(res => {
        if (!res.pending) return
        setPending(true)
        setUnreadConvId(res.unread_conversation_id)
        // Don't light the dot if the panel is already open in front of them.
        if (!stateRef.current.open) setHasUnread(true)
      })
      .catch(e => console.error('session-start', e))
  }, [])

  // Called by ChatPanel when the agent navigates the user. We want the page
  // visible: mobile = close the chat entirely; maximized desktop = drop to
  // the compact docked widget; already-docked = no-op.
  const onAgentNavigated = useCallback(() => {
    if (stateRef.current.mobile) setOpen(false)
    else if (stateRef.current.maximized) setMaximized(false)
  }, [])

  // Called by ChatPanel on assistant turn completion. If the panel isn't
  // visible to the user, light up the unread indicator on the FAB.
  const onAssistantDone = useCallback(() => {
    if (!stateRef.current.open) setHasUnread(true)
  }, [])

  return (
    <>
      <style>{`
/* ── The unread indicator runs on a single clock ────────────────────────────
   Everything below is timed off one "pulse" — the --mila-pulse custom property
   on the FAB. Each layer's duration is an integer multiple of it, so they all
   share a phase and re-sync on their least common multiple:

       ripple ring   ·  every  1 pulse   (the metronome)
       icon wiggle   ·  every  3 pulses
       bubble breathe·  every  5 pulses
       FULL SPIN     ·  every 15 pulses  ← LCM(3,5): wiggle and breathe line up,
                                            so the icon spins a full turn while
                                            the whole bubble swells extra-big
                                            instead of just nudging. Easter egg
                                            for anyone who counts the beats. ─── */

/* Expanding terracotta ripple ring radiating off the FAB — one per pulse. */
@keyframes mila-unread-ring {
  0%   { box-shadow: 0 0 0 0 rgba(184,83,57,0.45); }
  70%  { box-shadow: 0 0 0 18px rgba(184,83,57,0); }
  100% { box-shadow: 0 0 0 18px rgba(184,83,57,0); }
}
/* The whole bubble breathes out and back every 5 pulses, then swells extra on
   the 15th — its big peak (~92%) lands with the icon's spin, settling back to
   rest by 100% so the loop stays seamless. */
@keyframes mila-fab-breathe {
  0%, 25%  { transform: scale(1); }
  33%      { transform: scale(1.26); }  /* pulse 5 */
  41%, 58% { transform: scale(1); }
  67%      { transform: scale(1.26); }  /* pulse 10 */
  75%, 84% { transform: scale(1); }
  92%      { transform: scale(1.4); }  /* pulse 15 — bigger, with the spin */
  100%     { transform: scale(1); }
}
/* The icon's 15-pulse program: a quick wiggle at pulses 3/6/9/12, then at
   pulse 15 — where the 3- and 5-beats coincide — a full 360° revolution that
   swells in lockstep with the halo (peaks ~92%, settling back by 100% so the
   loop is seamless: rotate(360deg) ≡ rotate(0deg)). */
@keyframes mila-fab-icon {
  0%, 18%  { transform: rotate(0deg)   scale(1); }
  20%      { transform: rotate(-12deg) scale(1.12); }  /* pulse 3 */
  22%      { transform: rotate(8deg)   scale(1.05); }
  24%      { transform: rotate(-3deg)  scale(1.02); }
  26%, 38% { transform: rotate(0deg)   scale(1); }
  40%      { transform: rotate(-12deg) scale(1.12); }  /* pulse 6 */
  42%      { transform: rotate(8deg)   scale(1.05); }
  44%      { transform: rotate(-3deg)  scale(1.02); }
  46%, 58% { transform: rotate(0deg)   scale(1); }
  60%      { transform: rotate(-12deg) scale(1.12); }  /* pulse 9 */
  62%      { transform: rotate(8deg)   scale(1.05); }
  64%      { transform: rotate(-3deg)  scale(1.02); }
  66%, 78% { transform: rotate(0deg)   scale(1); }
  80%      { transform: rotate(-12deg) scale(1.12); }  /* pulse 12 */
  82%      { transform: rotate(8deg)   scale(1.05); }
  84%      { transform: rotate(-3deg)  scale(1.02); }
  86%      { transform: rotate(0deg)   scale(1); }
  92%      { transform: rotate(310deg) scale(1.12); }  /* pulse 15 — the spin */
  100%     { transform: rotate(360deg) scale(1); }
}
@media (prefers-reduced-motion: reduce) {
  /* Honour reduced-motion: keep the unread signal, drop the choreography. */
  [data-mila-fab] *, [data-mila-fab] { animation: none !important; }
}
`}</style>
      <button
        onClick={() => setOpen(true)}
        aria-label={hasUnread ? 'Open Mila chat (new message)' : 'Open Mila chat'}
        aria-hidden={open}
        tabIndex={open ? -1 : 0}
        data-mila-fab=""
        style={{
          all: 'unset', cursor: 'pointer',
          position: 'fixed',
          right: mobile ? 14 : 24, bottom: mobile ? 78 : 24,
          width: 56, height: 56, borderRadius: 999,
          background: theme.ink, color: theme.surface,
          display: 'grid', placeItems: 'center',
          boxShadow: '0 8px 24px rgba(20,14,8,0.30)',
          zIndex: 31,
          opacity: open ? 0 : 1,
          transform: open ? 'scale(0.6)' : 'scale(1)',
          pointerEvents: open ? 'none' : 'auto',
          transition: 'opacity 180ms ease, transform 220ms cubic-bezier(0.22, 1, 0.36, 1)',
          // The whole bubble breathes every 5 pulses (extra-big on the 15th).
          // While breathing, this animation owns transform; the open/close
          // scale above takes back over the moment the animation stops.
          animation: hasUnread && !open ? 'mila-fab-breathe calc(var(--mila-pulse) * 15) ease-in-out infinite' : undefined,
          // The one knob that sets the tempo for every layer below. Tweak here.
          ['--mila-pulse' as string]: '1.167s',
        } as React.CSSProperties}
      >
        {/* While a reply waits, ripple rings fire every pulse. */}
        {hasUnread && !open && (
          <>
            <span aria-hidden="true" style={{
              position: 'absolute', inset: 0, borderRadius: 999, pointerEvents: 'none',
              animation: 'mila-unread-ring calc(var(--mila-pulse) * 2) ease-out infinite',
            }}/>
            <span aria-hidden="true" style={{
              position: 'absolute', inset: 0, borderRadius: 999, pointerEvents: 'none',
              animation: 'mila-unread-ring calc(var(--mila-pulse) * 2) ease-out var(--mila-pulse) infinite',
            }}/>
          </>
        )}
        {/* Icon wiggles every 3 pulses, and spins a full turn every 15th. */}
        <span style={{
          display: 'grid', placeItems: 'center',
          animation: hasUnread && !open ? 'mila-fab-icon calc(var(--mila-pulse) * 15) ease-in-out infinite' : undefined,
        }}>
          <Ic.chat width="22" height="22"/>
        </span>
      </button>
      {mounted && (
        <ChatPanel
          layout={layout}
          visible={visible}
          pending={pending}
          unreadConvId={unreadConvId}
          onCollapse={() => setOpen(false)}
          onToggleMaximize={() => setMaximized(m => !m)}
          onAgentNavigated={onAgentNavigated}
          onAssistantDone={onAssistantDone}
        />
      )}
    </>
  )
}

// ============================================================================
// ChatPanel
// ============================================================================

function ChatPanel({
  layout,
  visible,
  pending,
  unreadConvId,
  onCollapse,
  onToggleMaximize,
  onAgentNavigated,
  onAssistantDone,
}: {
  layout: Layout
  visible: boolean
  pending: boolean
  unreadConvId: string | null
  onCollapse: () => void
  onToggleMaximize: () => void
  onAgentNavigated: () => void
  onAssistantDone: () => void
}) {
  const [conversations, setConversations] = useState<ChatConversation[]>([])
  const [convId, setConvId] = useState<string | null>(null)
  const [conv, setConv] = useState<{ title: string } | null>(null)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [draft, setDraft] = useState('')
  const [pendingImage, setPendingImage] = useState<{ base64: string; mimeType: string; dataUrl: string } | null>(null)
  const [sending, setSending] = useState(false)
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const galleryInputRef = useRef<HTMLInputElement>(null)
  const [showDock, setShowDock] = useState(layout === 'maximized')
  const [dockMounted, setDockMounted] = useState(layout === 'maximized')
  const [dockVisible, setDockVisible] = useState(layout === 'maximized')
  const [activeStream, setActiveStream] = useState<{ messageId: string } | null>(null)
  // A queued agent navigation awaiting the user's tap. Its card pulses for
  // attention indefinitely; tapping it collapses the chat and routes (see
  // resolveClientTool and the navigate handler below).
  const [pendingNav, setPendingNav] = useState<{ callId: string; path: string } | null>(null)
  const [alertMsg, setAlertMsg] = useState<string | null>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // "Last read" divider. `dividerAfterId` is the message the line sits *after*;
  // it's frozen when a conversation is opened/revealed so the line doesn't jump
  // as you read or as a reply streams in. `markedReadRef` is the newest message
  // we've told the server is read — it doubles as the anchor for recomputing the
  // divider when the panel is reopened on an already-loaded conversation.
  const [dividerAfterId, setDividerAfterId] = useState<string | null>(null)
  const markedReadRef = useRef<string | null>(null)
  const prevVisibleRef = useRef(visible)
  // Latest visible/convId for the SSE callback, which must stay referentially
  // stable (see the ChatBubble comment about SSE teardown) — so it reads these
  // through a ref rather than taking them as effect deps.
  const liveRef = useRef({ visible, convId })
  liveRef.current = { visible, convId }

  const markRead = useCallback((cid: string, messageId: string | null) => {
    if (!cid || !messageId || markedReadRef.current === messageId) return
    markedReadRef.current = messageId
    api.chatMarkRead(cid, messageId).catch(e => console.error('markRead', e))
  }, [])

  // The user tapped the pulsing navigate card. Disarm it, collapse the chat so
  // the destination is visible, then change the route a beat later.
  const triggerNav = useCallback(() => {
    if (!pendingNav) return
    const { path } = pendingNav
    setPendingNav(null)
    onAgentNavigated()
    setTimeout(() => routeTo(path), NAV_ROUTE_DELAY)
  }, [pendingNav, onAgentNavigated])

  // History dock default per layout.
  useEffect(() => { setShowDock(layout === 'maximized') }, [layout])

  // Two-phase mount for the dock so it can transition in/out.
  useEffect(() => {
    if (showDock) {
      setDockMounted(true)
      let id2 = 0
      const id1 = requestAnimationFrame(() => {
        id2 = requestAnimationFrame(() => setDockVisible(true))
      })
      return () => { cancelAnimationFrame(id1); cancelAnimationFrame(id2) }
    }
    setDockVisible(false)
    const t = window.setTimeout(() => setDockMounted(false), 360)
    return () => window.clearTimeout(t)
  }, [showDock])

  const refreshList = useCallback(async () => {
    try { setConversations(await api.chatConversations()) }
    catch (e) { console.error(e) }
  }, [])

  useEffect(() => { refreshList() }, [refreshList])

  const loadConversation = useCallback(async (id: string) => {
    try {
      const c = await api.chatConversation(id)
      setConvId(c.id)
      setConv({ title: c.title })
      // Defend against a malformed row: a message whose `parts` isn't an array
      // (e.g. a legacy turn persisted as JSON `null`) would otherwise throw
      // "parts is not iterable" in MessageView and unmount the whole SPA.
      setMessages(c.messages.map(m => Array.isArray(m.parts) ? m : { ...m, parts: [] }))
      markedReadRef.current = null
      // Freeze the divider at the stored watermark — but only when there's
      // actually something past it. A null watermark (brand-new conversation)
      // or one pointing at the last message means nothing unread → no line.
      const watermark = c.last_read_message_id
      const wi = watermark ? c.messages.findIndex(m => m.id === watermark) : -1
      setDividerAfterId(wi >= 0 && wi < c.messages.length - 1 ? watermark : null)
      const last = c.messages[c.messages.length - 1]
      if (last && last.role === 'assistant' && last.status === 'streaming') {
        setActiveStream({ messageId: last.id })
      } else {
        setActiveStream(null)
      }
      // Opening the conversation reads its newest settled message.
      const newestSettled = [...c.messages].reverse().find(m => m.status !== 'streaming')
      if (newestSettled) markRead(c.id, newestSettled.id)
    } catch (e) { console.error(e) }
  }, [markRead])

  // When the user opens the chat on a fresh, empty conversation and there's
  // something to nudge about, surface it right then — never on page load. A
  // pending tour generates a proactive greeting (a throwaway draft server-side,
  // not saved to history unless they reply, so each open yields a clean, current
  // greeting). If instead the nudge is an unread reply in a real conversation,
  // there's no greeting to generate — re-open that conversation directly so the
  // user actually lands on the message the FAB was pulsing about. Fires at most
  // once per mount; a page refresh resets it. If there's nothing to show, the
  // panel just shows the empty "ask me anything" state.
  const didGreetRef = useRef(false)
  useEffect(() => {
    if (didGreetRef.current || !visible || !pending || convId !== null || messages.length > 0) return
    didGreetRef.current = true
    api.chatStartGreeting()
      .then(res => {
        if (res.conversation_id) loadConversation(res.conversation_id)
        else if (unreadConvId) loadConversation(unreadConvId)
      })
      .catch(e => console.error('greeting', e))
  }, [visible, pending, convId, messages.length, unreadConvId, loadConversation])

  const newConversation = useCallback(() => {
    // No-op if we're already on an empty conversation. The backend row is
    // only created when the first message is sent (see `send`), so this also
    // avoids persisting empty conversations to the DB.
    if (convId === null && messages.length === 0) return
    setConvId(null)
    setConv(null)
    setMessages([])
    setActiveStream(null)
    setDividerAfterId(null)
    markedReadRef.current = null
  }, [convId, messages.length])

  // Stream consumer.
  useEffect(() => {
    if (!activeStream) return
    const ctrl = new AbortController()
    const mid = activeStream.messageId
    consumeSSE(
      `/api/chat/messages/${mid}/stream`,
      (ev) => {
        if (ev.type === 'client_tool_call') {
          resolveClientTool(mid, ev.data, setPendingNav)
        }
        setMessages(prev => prev.map(m => {
          if (m.id !== mid) return m
          if (ev.type === 'message_complete')
            return { ...m, status: 'complete' }
          if (ev.type === 'error')
            return { ...m, status: 'error', error: ev.data?.message ?? 'Something went wrong.' }
          return { ...m, parts: applyEvent(m.parts, ev) }
        }))
        if (ev.type === 'message_complete' || ev.type === 'error') {
          setActiveStream(null)
          refreshList().catch(() => {})
          if (ev.type === 'message_complete') {
            onAssistantDone()
            // If the user is actually watching this conversation, the reply is
            // read on arrival — advance the watermark so it stays below any
            // divider and other devices see it cleared.
            const { visible: vis, convId: cid } = liveRef.current
            if (vis && cid) markRead(cid, mid)
          }
        }
      },
      ctrl.signal,
    ).catch(err => {
      if (ctrl.signal.aborted) return
      console.error('stream', err)
    })
    return () => ctrl.abort()
  }, [activeStream, refreshList, onAssistantDone, markRead, setPendingNav])

  // Auto-scroll on new content.
  useEffect(() => {
    const el = listRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [messages])

  // Panel reopened on a conversation that's still loaded (the common FAB flow:
  // ask Mila, collapse, a reply streams in while closed, reopen). loadConversation
  // didn't re-run, so recompute the divider from the last message we acknowledged
  // as read, then mark the now-seen reply read.
  useEffect(() => {
    const wasVisible = prevVisibleRef.current
    prevVisibleRef.current = visible
    if (!visible || wasVisible || !convId) return
    const anchor = markedReadRef.current
    const ai = anchor ? messages.findIndex(m => m.id === anchor) : -1
    setDividerAfterId(ai >= 0 && ai < messages.length - 1 ? anchor : null)
    const newestSettled = [...messages].reverse().find(m => m.status !== 'streaming')
    if (newestSettled) markRead(convId, newestSettled.id)
  }, [visible, convId, messages, markRead])

  // Auto-grow textarea up to its max-height (~3 lines).
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [draft])

  const handleImageFile = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    const mimeType = file.type || 'image/jpeg'
    const reader = new FileReader()
    reader.onload = () => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        const MAX = 1600
        let { width, height } = img
        if (width > MAX || height > MAX) {
          if (width > height) { height = Math.round((height * MAX) / width); width = MAX }
          else { width = Math.round((width * MAX) / height); height = MAX }
        }
        canvas.width = width
        canvas.height = height
        canvas.getContext('2d')!.drawImage(img, 0, 0, width, height)
        const outMime = mimeType === 'image/png' ? 'image/png' : 'image/jpeg'
        const dataUrl = canvas.toDataURL(outMime, 0.85)
        const base64 = dataUrl.split(',')[1]
        setPendingImage({ base64, mimeType: outMime, dataUrl })
      }
      img.src = reader.result as string
    }
    reader.readAsDataURL(file)
  }, [])

  const send = useCallback(async () => {
    const text = draft.trim()
    if ((!text && !pendingImage) || sending) return
    setSending(true)
    try {
      let cid = convId
      const createdNew = !cid
      if (!cid) {
        const { id } = await api.chatNewConversation()
        cid = id
        setConvId(id)
        setConv({ title: '' })
      }
      const clientId = (crypto as any).randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random()
      const img = pendingImage
      // Optimistically add user message.
      const tempParts: any[] = []
      if (img) tempParts.push({ type: 'image', image_id: -1, mime_type: img.mimeType, preview_url: img.dataUrl })
      if (text) tempParts.push({ type: 'text', text })
      const tempUser: ChatMessage = {
        id: 'tmp-' + clientId, role: 'user',
        parts: tempParts,
        status: 'complete',
        created_at: new Date().toISOString(),
      }
      setMessages(prev => [...prev, tempUser])
      setDraft('')
      setPendingImage(null)

      const { user_message_id, assistant_message_id } = await api.chatPostMessage(
        cid, text, clientId,
        img ? { base64: img.base64, mime_type: img.mimeType } : undefined,
      )
      // Swap temp user msg id and add assistant placeholder.
      setMessages(prev => {
        const next = prev.map(m => m.id === tempUser.id ? { ...m, id: user_message_id } : m)
        next.push({
          id: assistant_message_id,
          role: 'assistant',
          parts: [],
          status: 'streaming',
          created_at: new Date().toISOString(),
        })
        return next
      })
      setActiveStream({ messageId: assistant_message_id })
      // We're actively in this conversation — clear any leftover divider, and
      // mark our own message read so a divider would land before the reply if
      // we leave before it finishes.
      setDividerAfterId(null)
      markRead(cid, user_message_id)
      if (createdNew) refreshList().catch(() => {})
    } catch (e) {
      console.error(e)
      setAlertMsg('Failed to send')
    } finally {
      setSending(false)
    }
  }, [draft, sending, convId, pendingImage, refreshList, markRead])

  // Re-run the last assistant turn after a failure. The backend drops the
  // errored message and starts a fresh one; mirror that in local state, then
  // stream the replacement.
  const retry = useCallback(async () => {
    if (!convId) return
    try {
      const { assistant_message_id } = await api.chatRetry(convId)
      setMessages(prev => {
        const next = [...prev]
        while (next.length) {
          const last = next[next.length - 1]
          if (last.role === 'assistant' && (last.status === 'error' || last.status === 'cancelled')) next.pop()
          else break
        }
        next.push({
          id: assistant_message_id,
          role: 'assistant',
          parts: [],
          status: 'streaming',
          created_at: new Date().toISOString(),
        })
        return next
      })
      setActiveStream({ messageId: assistant_message_id })
    } catch (e) {
      console.error('retry', e)
      setAlertMsg('Retry failed')
    }
  }, [convId])

  const cancel = useCallback(async () => {
    if (!activeStream) return
    try { await api.chatCancel(activeStream.messageId) }
    catch (e) { console.error(e) }
  }, [activeStream])

  // ---- layout shells -----------------------------------------------------
  //
  // Single style object across all layouts so the panel can transition
  // smoothly when toggling between docked and maximized. Positioning is done
  // via right/bottom/width/height (not inset:0 vs auto) so every property is
  // animatable.

  const isFullscreen = layout === 'mobile' || layout === 'maximized'
  const transition =
    'opacity 320ms ease, ' +
    'transform 380ms cubic-bezier(0.22, 1, 0.36, 1), ' +
    'width 380ms cubic-bezier(0.22, 1, 0.36, 1), ' +
    'height 380ms cubic-bezier(0.22, 1, 0.36, 1), ' +
    'right 380ms cubic-bezier(0.22, 1, 0.36, 1), ' +
    'bottom 380ms cubic-bezier(0.22, 1, 0.36, 1), ' +
    'border-radius 360ms ease, ' +
    'box-shadow 360ms ease'

  const panelStyle: React.CSSProperties = {
    position: 'fixed',
    top: 'auto', left: 'auto',
    right: isFullscreen ? 0 : 24,
    bottom: isFullscreen ? 0 : 24,
    width: isFullscreen ? '100dvw' : 380,
    height: isFullscreen ? '100dvh' : 640,
    maxHeight: isFullscreen ? '100dvh' : 'calc(100dvh - 48px)',
    background: theme.surface,
    borderRadius: isFullscreen ? 0 : theme.radiusLg,
    border: theme.cardBorder,
    boxShadow: isFullscreen ? '0 0 0 rgba(20,14,8,0)' : '0 16px 50px rgba(20,14,8,0.30)',
    display: 'flex', flexDirection: 'row',
    overflow: 'hidden',
    zIndex: 40,
    opacity: visible ? 1 : 0,
    pointerEvents: visible ? 'auto' : 'none',
    transform: visible ? 'scale(1) translateY(0)' : 'scale(0.88) translateY(24px)',
    transformOrigin: 'bottom right',
    transition,
    // Disable pinch-zoom inside the chat panel when it occupies the full
    // viewport — accidental zooms while typing/scrolling were stranding the
    // sides of the UI off-screen. Use the global "Display size" setting on
    // the Preferences tab for accessibility scaling instead.
    touchAction: isFullscreen ? 'pan-y' : undefined,
  }

  const dockFixedLeft = layout === 'maximized'
  const dockEasing = 'cubic-bezier(0.22, 1, 0.36, 1)'

  return (
    <div style={panelStyle}>
      {dockFixedLeft && dockMounted && (
        <div style={{
          flex: '0 0 auto',
          width: dockVisible ? 260 : 0,
          opacity: dockVisible ? 1 : 0,
          overflow: 'hidden',
          transition: `width 320ms ${dockEasing}, opacity 240ms ease`,
        }}>
          <div style={{ width: 260, height: '100%' }}>
            <HistoryDock
              conversations={conversations}
              activeId={convId}
              onPick={(id) => { loadConversation(id); }}
              onNew={() => newConversation()}
              onDelete={async (id) => {
                await api.chatDeleteConversation(id)
                if (id === convId) { setConvId(null); setMessages([]); setConv(null) }
                await refreshList()
              }}
              variant="rail"
            />
          </div>
        </div>
      )}

      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', position: 'relative' }}>
        {/* Header */}
        <div style={{
          padding: '10px 12px', borderBottom: `1px solid ${theme.line}`,
          display: 'flex', alignItems: 'center', gap: 8, flex: '0 0 auto',
        }}>
          <button onClick={() => setShowDock(s => !s)} title="Conversations" style={iconBtn}>
            <Ic.filter width="18" height="18"/>
          </button>
          <div style={{
            flex: 1, fontFamily: theme.serif, fontSize: 16, fontWeight: theme.headWeight,
            color: theme.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {conv?.title || ''}
          </div>
          {layout !== 'mobile' && (
            <button onClick={onToggleMaximize} title={layout === 'maximized' ? 'Restore' : 'Maximize'} style={iconBtn}>
              {layout === 'maximized'
                ? <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 9h6v6H9z"/></svg>
                : <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 4h6M4 4v6M20 4h-6M20 4v6M4 20h6M4 20v-6M20 20h-6M20 20v-6"/></svg>}
            </button>
          )}
          <button onClick={onCollapse} title="Collapse" style={iconBtn}>
            <Ic.chevD width="18" height="18"/>
          </button>
        </div>

        {/* Slide-over dock (non-maximized) */}
        {!dockFixedLeft && dockMounted && (
          <>
            <div onClick={() => setShowDock(false)} style={{
              position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.05)', zIndex: 2,
              opacity: dockVisible ? 1 : 0,
              transition: 'opacity 240ms ease',
            }}/>
            <div style={{
              position: 'absolute', top: 0, bottom: 0, left: 0, width: '80%', maxWidth: 280,
              background: theme.surface, borderRight: `1px solid ${theme.line}`,
              zIndex: 3, display: 'flex', flexDirection: 'column',
              transform: dockVisible ? 'translateX(0)' : 'translateX(-100%)',
              transition: `transform 320ms ${dockEasing}`,
            }}>
              <HistoryDock
                conversations={conversations}
                activeId={convId}
                onPick={(id) => { loadConversation(id); setShowDock(false) }}
                onNew={() => { newConversation(); setShowDock(false) }}
                onDelete={async (id) => {
                  await api.chatDeleteConversation(id)
                  if (id === convId) { setConvId(null); setMessages([]); setConv(null) }
                  await refreshList()
                }}
                variant="overlay"
              />
            </div>
          </>
        )}

        {/* Message list */}
        <div ref={listRef} style={{
          flex: 1, minHeight: 0, overflowY: 'auto',
          padding: '14px 14px 8px',
          display: 'flex', flexDirection: 'column', gap: 12,
        }}>
          {messages.length === 0 && (
            <div style={{
              margin: 'auto', textAlign: 'center', maxWidth: 280,
              color: theme.muted, fontFamily: theme.sans, fontSize: 13,
            }}>
              <img src="/mila-name-transparent.png" alt="Mila" style={{ height: 80, width: 'auto', display: 'block', margin: '0 auto 14px' }}/>
              <div style={{ fontFamily: theme.serif, fontSize: 18, color: theme.inkSoft, marginBottom: 6 }}>
                Ask me about your cookbook
              </div>
              <div>Try: "add a recipe for shakshuka" or "find recipes with chicken".</div>
            </div>
          )}
          {(() => {
            // Divider sits before the first message after `dividerAfterId`.
            const di = dividerAfterId ? messages.findIndex(m => m.id === dividerAfterId) : -1
            const dividerIdx = di >= 0 && di < messages.length - 1 ? di + 1 : -1
            return messages.flatMap((m, i) => {
              const canRetry = i === messages.length - 1 && m.role === 'assistant' && m.status === 'error'
              const node = <MessageView key={m.id} m={m} navCallId={pendingNav?.callId ?? null} onNavigate={triggerNav} onRetry={canRetry ? retry : undefined}/>
              return i === dividerIdx ? [<NewDivider key="new-divider"/>, node] : [node]
            })
          })()}
          {activeStream && (
            <div style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 6, color: theme.muted, fontSize: 12 }}>
              <Spinner/> thinking
            </div>
          )}
        </div>

        {/* Input */}
        <div style={{
          padding: 10, borderTop: `1px solid ${theme.line}`,
          display: 'flex', flexDirection: 'column', gap: 8, flex: '0 0 auto',
          paddingBottom: layout === 'mobile' ? 'calc(10px + env(safe-area-inset-bottom))' : 10,
        }}>
          {pendingImage && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '6px 8px', background: theme.surface2,
              border: `1px solid ${theme.line}`, borderRadius: theme.radius,
            }}>
              <img src={pendingImage.dataUrl} alt="attached" style={{
                width: 44, height: 44, objectFit: 'cover',
                borderRadius: theme.radius, border: `1px solid ${theme.line}`,
              }}/>
              <div style={{
                flex: 1, fontFamily: theme.sans, fontSize: 12, color: theme.muted,
              }}>Image attached</div>
              <button onClick={() => setPendingImage(null)} title="Remove" style={iconBtn}>
                <Ic.close width="16" height="16"/>
              </button>
            </div>
          )}
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <input
            ref={cameraInputRef} type="file" accept="image/*" capture="environment"
            style={{ display: 'none' }} onChange={handleImageFile}
          />
          <input
            ref={galleryInputRef} type="file" accept="image/*"
            style={{ display: 'none' }} onChange={handleImageFile}
          />
          <button onClick={newConversation} title="New chat" style={iconBtn}>
            <Ic.plus width="18" height="18"/>
          </button>
          <button onClick={() => cameraInputRef.current?.click()} title="Take a photo" style={iconBtn}>
            <Ic.camera width="18" height="18"/>
          </button>
          <button onClick={() => galleryInputRef.current?.click()} title="Attach image" style={iconBtn}>
            <Ic.image width="18" height="18"/>
          </button>
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send() }
            }}
            placeholder="Message Mila…"
            rows={1}
            style={{
              flex: 1, resize: 'none',
              padding: '8px 10px', minHeight: 36, maxHeight: 76,
              lineHeight: '20px',
              fontFamily: theme.sans, fontSize: 14, color: theme.ink,
              border: `1px solid ${theme.line}`, borderRadius: theme.radius,
              background: theme.surface2, outline: 'none',
              overflowY: 'auto',
            }}
          />
          {activeStream ? (
            <button onClick={cancel} title="Stop" style={{ ...iconBtn, color: theme.accent }}>
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2"/></svg>
            </button>
          ) : (
            <button
              onClick={send}
              disabled={(!draft.trim() && !pendingImage) || sending}
              title="Send"
              style={{
                ...iconBtn,
                background: (draft.trim() || pendingImage) ? theme.ink : 'transparent',
                color: (draft.trim() || pendingImage) ? theme.surface : theme.muted,
                opacity: sending ? 0.5 : 1,
              }}>
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="m4 12 16-7-7 16-2-7-7-2Z"/>
              </svg>
            </button>
          )}
        </div>
        </div>
      </div>
      <AlertDialog
        open={alertMsg !== null}
        message={alertMsg ?? ''}
        onClose={() => setAlertMsg(null)}
      />
    </div>
  )
}

// ============================================================================
// HistoryDock
// ============================================================================

function HistoryDock({
  conversations, activeId, onPick, onNew, onDelete, variant,
}: {
  conversations: ChatConversation[]
  activeId: string | null
  onPick: (id: string) => void
  onNew: () => void
  onDelete: (id: string) => void
  variant: 'rail' | 'overlay'
}) {
  const [confirmId, setConfirmId] = useState<string | null>(null)
  return (
    <div style={{
      width: variant === 'rail' ? 260 : '100%', flex: '0 0 auto',
      borderRight: variant === 'rail' ? `1px solid ${theme.line}` : undefined,
      background: theme.surface, display: 'flex', flexDirection: 'column',
      height: '100%',
    }}>
      <div style={{ padding: 10, borderBottom: `1px solid ${theme.line}` }}>
        <button onClick={onNew} style={{
          all: 'unset', cursor: 'pointer', boxSizing: 'border-box', width: '100%',
          padding: '8px 10px', background: theme.ink, color: theme.surface,
          borderRadius: theme.radius, textAlign: 'center', fontSize: 13,
          fontFamily: theme.sans, fontWeight: 600,
        }}>+ New chat</button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}>
        {conversations.length === 0 && (
          <div style={{ padding: 14, color: theme.muted, fontSize: 12, fontFamily: theme.sans }}>
            No conversations yet.
          </div>
        )}
        {conversations.map(c => (
          <div key={c.id}
            onClick={() => onPick(c.id)}
            style={{
              padding: '8px 12px', cursor: 'pointer',
              background: c.id === activeId ? theme.surface2 : 'transparent',
              display: 'flex', alignItems: 'center', gap: 6,
              fontFamily: theme.sans, fontSize: 13, color: theme.ink,
            }}>
            <div style={{
              flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{c.title || '(untitled)'}</div>
            <button
              onClick={(e) => { e.stopPropagation(); setConfirmId(c.id) }}
              style={{ all: 'unset', cursor: 'pointer', color: theme.muted, padding: 4 }}
              title="Delete"
            >
              <Ic.trash width="14" height="14"/>
            </button>
          </div>
        ))}
      </div>
      <ConfirmDialog
        open={confirmId !== null}
        message="Delete conversation?"
        confirmLabel="Delete"
        danger
        onConfirm={() => {
          const id = confirmId
          setConfirmId(null)
          if (id) onDelete(id)
        }}
        onCancel={() => setConfirmId(null)}
      />
    </div>
  )
}

// ============================================================================
// MessageView, ToolCallCard
// ============================================================================

function MessageView({ m, navCallId, onNavigate, onRetry }: { m: ChatMessage; navCallId?: string | null; onNavigate?: () => void; onRetry?: () => void }) {
  if (m.role === 'user') {
    const text = m.parts.map(p => p.type === 'text' ? p.text : '').join('')
    const images = m.parts.filter(p => p.type === 'image') as Extract<ChatPart, { type: 'image' }>[]
    return (
      <div style={{
        alignSelf: 'flex-end', maxWidth: '85%',
        display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end',
      }}>
        {images.map((img, i) => {
          const src = img.preview_url ?? `/api/chat/images/${img.image_id}`
          return (
            <img key={i} src={src} alt="attached" style={{
              maxWidth: 240, maxHeight: 240, objectFit: 'cover',
              borderRadius: 12, border: `1px solid ${theme.line}`,
            }}/>
          )
        })}
        {text && (
          <div style={{
            background: theme.ink, color: theme.surface,
            padding: '8px 12px', borderRadius: 12,
            fontFamily: theme.sans, fontSize: 14, lineHeight: 1.45,
            whiteSpace: 'pre-wrap',
          }}>{text}</div>
        )}
      </div>
    )
  }
  if (m.role !== 'assistant') return null

  // Render parts in order. Tool_result parts are absorbed by their tool_call.
  const resultByCallId = new Map<string, Extract<ChatPart, { type: 'tool_result' }>>()
  for (const p of m.parts) {
    if (p.type === 'tool_result') resultByCallId.set(p.tool_call_id, p)
  }

  return (
    <div style={{
      alignSelf: 'flex-start', maxWidth: '92%',
      display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      {m.parts.map((p, i) => {
        if (p.type === 'text') {
          return (
            <div key={i} className="mila-md" style={{
              fontFamily: theme.sans, fontSize: 14, color: theme.ink, lineHeight: 1.5,
            }}>
              <Markdown>{p.text}</Markdown>
            </div>
          )
        }
        if (p.type === 'tool_call') {
          const result = resultByCallId.get(p.id)
          return <ToolCallCard key={i} call={p} result={result} navPending={p.id === navCallId} onNavigate={onNavigate}/>
        }
        return null // tool_result rendered inside its call
      })}
      {m.status === 'error' && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          padding: '8px 10px', borderRadius: theme.radius,
          background: theme.surface2, border: `1px solid ${theme.line}`,
        }}>
          <span style={{ fontFamily: theme.sans, fontSize: 13, color: theme.muted }}>
            {m.error ?? 'Mila’s assistant ran into a problem.'}
          </span>
          {onRetry && (
            <button onClick={onRetry} style={{
              fontFamily: theme.sans, fontSize: 13, fontWeight: 600,
              color: theme.accent, background: 'transparent',
              border: `1px solid ${theme.accent}`, borderRadius: theme.radius,
              padding: '4px 12px', cursor: 'pointer',
            }}>Retry</button>
          )}
        </div>
      )}
    </div>
  )
}

// A gated tool reaches the model as invoke_tool({name, args}). For display we
// unwrap it so the card reads like a direct call to the inner tool, and flag
// viaInvoke so we can mark that it was dispatched through the omnitool.
function displayCall(call: Extract<ChatPart, { type: 'tool_call' }>):
  { name: string; args: unknown; viaInvoke: boolean } {
  if (call.name === 'invoke_tool' && call.args && typeof call.args === 'object') {
    const inner = call.args as { name?: unknown; args?: unknown }
    if (typeof inner.name === 'string') {
      return { name: inner.name, args: inner.args ?? {}, viaInvoke: true }
    }
  }
  return { name: call.name, args: call.args, viaInvoke: false }
}

// effectiveStatus resolves the card's dot color. invoke_tool always returns
// "success" at the transport level (the dispatcher catches inner errors), so we
// inspect the result payload: `invalid:true` = a malformed/bad dispatch the
// model should fix (amber), a plain `error` = the inner tool genuinely failed
// (red). Direct tools fall back to the streamed call.status.
function effectiveStatus(
  call: Extract<ChatPart, { type: 'tool_call' }>,
  result?: Extract<ChatPart, { type: 'tool_result' }>,
): 'running' | 'succeeded' | 'failed' | 'invalid' {
  if (call.status === 'running') return 'running'
  if (result?.error) return 'failed'
  const r = result?.result
  if (r && typeof r === 'object') {
    const o = r as Record<string, unknown>
    if (o.invalid === true) return 'invalid'
    if (typeof o.error === 'string') return 'failed'
  }
  return call.status === 'failed' ? 'failed' : 'succeeded'
}

function ToolCallCard({
  call, result, navPending, onNavigate,
}: {
  call: Extract<ChatPart, { type: 'tool_call' }>
  result?: Extract<ChatPart, { type: 'tool_result' }>
  navPending?: boolean
  onNavigate?: () => void
}) {
  const [open, setOpen] = useState(false)
  const { name, args, viaInvoke } = displayCall(call)
  const status = effectiveStatus(call, result)
  const dot = status === 'running' ? theme.muted
    : status === 'invalid' ? theme.butter
    : status === 'failed' ? theme.accent
    : theme.olive
  // Surface the human-readable error (inner-tool failure or bad-dispatch), not
  // the raw {error, schema, …} envelope, in the collapsed→expanded view.
  const shownError = (() => {
    if (result?.error) return result.error
    const r = result?.result
    if (r && typeof r === 'object' && typeof (r as any).error === 'string') return (r as any).error as string
    return null
  })()
  const summary = (() => {
    if (!args || typeof args !== 'object') return ''
    const obj = args as Record<string, any>
    return Object.entries(obj).slice(0, 2).map(([k, v]) => `${k}=${JSON.stringify(v)}`).join(' ')
  })()
  return (
    <div style={{
      border: `1px solid ${navPending ? theme.accent : theme.line}`, borderRadius: theme.radius,
      background: theme.surface2, fontFamily: theme.sans, fontSize: 12,
      // Pulse for attention indefinitely while waiting for the user to tap.
      animation: navPending ? `mila-nav-pulse ${NAV_PULSE_DURATION}ms ease-in-out infinite` : undefined,
    }}>
      {navPending && <style>{`@keyframes mila-nav-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(184,83,57,0); transform: scale(1); }
  35%      { box-shadow: 0 0 0 5px rgba(184,83,57,0.22); transform: scale(1.025); }
}`}</style>}
      {/* While a navigation is queued, the whole card becomes a tap-to-go button
          rather than the args expander. */}
      <button onClick={() => navPending ? onNavigate?.() : setOpen(o => !o)} style={{
        all: 'unset', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
        width: '100%', padding: '6px 10px', boxSizing: 'border-box',
      }}>
        {status === 'running'
          ? <Spinner/>
          : <div style={{ width: 8, height: 8, borderRadius: 999, background: navPending ? theme.accent : dot }}/>}
        <code style={{ fontFamily: theme.mono, color: theme.inkSoft }}>{name}</code>
        <span style={{ color: theme.muted, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {summary}
        </span>
        {navPending
          ? <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: theme.accent, fontWeight: 600, whiteSpace: 'nowrap' }}>
              Tap to go <Ic.chevD width="14" height="14" style={{ transform: 'rotate(-90deg)' }}/>
            </span>
          : <Ic.chevD width="14" height="14" style={{ color: theme.muted, transform: open ? 'rotate(180deg)' : undefined }}/>}
      </button>
      {open && !navPending && (
        <div style={{ padding: '6px 10px 10px', borderTop: `1px solid ${theme.line}` }}>
          {viaInvoke && (
            <div style={{ color: theme.muted, fontSize: 11, marginBottom: 2 }}>
              dispatched via <code style={{ fontFamily: theme.mono }}>invoke_tool</code>
            </div>
          )}
          <Section label="args" value={args}/>
          {shownError
            ? <Section label="error" value={shownError}/>
            : result && <Section label="result" value={result.result}/>}
        </div>
      )}
    </div>
  )
}

function Section({ label, value }: { label: string; value: any }) {
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ color: theme.muted, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.6 }}>{label}</div>
      <pre style={{
        margin: 0, padding: 6, background: theme.surface,
        border: `1px solid ${theme.line}`, borderRadius: theme.radius,
        fontFamily: theme.mono, fontSize: 11, color: theme.ink,
        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        maxHeight: 240, overflow: 'auto',
      }}>
        {typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
      </pre>
    </div>
  )
}

// The "last read" marker: a thin terracotta rule with a centered label,
// shown before the first message that arrived since the conversation was read.
function NewDivider() {
  const rule: React.CSSProperties = { flex: 1, height: 1, background: theme.accent, opacity: 0.3 }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '2px 2px' }}>
      <div style={rule}/>
      <span style={{
        fontFamily: theme.sans, fontSize: 10, fontWeight: 700,
        letterSpacing: 1, textTransform: 'uppercase', color: theme.accent,
      }}>New</span>
      <div style={rule}/>
    </div>
  )
}

function Spinner() {
  return (
    <div style={{
      width: 10, height: 10, borderRadius: 999,
      border: `2px solid ${theme.line}`, borderTopColor: theme.inkSoft,
      animation: 'mila-spin 0.8s linear infinite',
    }}>
      <style>{`@keyframes mila-spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  )
}

const iconBtn: React.CSSProperties = {
  all: 'unset', cursor: 'pointer',
  padding: 6, borderRadius: theme.radius,
  color: theme.inkSoft,
  display: 'inline-grid', placeItems: 'center',
}
